import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Booking drawer for voyage PDPs.
 *
 * Extends the native DialogComponent but overrides open/close to lock scroll via
 * the `html[scroll-lock]` attribute (as the nav drawer does) instead of the default
 * `body { position: fixed }` trick. The body-fixed approach resets the document
 * scrollTop, which the sticky header reads as a scroll and uses to toggle
 * `data-scroll-direction`. The attribute approach preserves scroll position.
 */
class VoyageBooking extends DialogComponent {
  /** Scopes the open-dialog document/self listeners so they auto-remove on close. */
  #dialogController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleTriggerClick);

    // Booking data island (rooms, nightly variants, min/max nights). See voyage-booking.liquid.
    this.bookingData = null;
    const dataEl = this.querySelector('#voyage-booking-data');
    if (dataEl) {
      try {
        this.bookingData = JSON.parse(dataEl.textContent);
      } catch {
        this.bookingData = null;
      }
    }

    // Runtime strings island (Liquid `| t` resolved). Falls back to French if absent.
    this.i18n = {
      perNight: '/nuit',
      max: 'Max',
      nextRoom: 'Choisir la chambre suivante',
      seeRecap: 'Voir le récapitulatif',
      departure: 'Départ',
      minStay: 'Séjour minimum non atteint',
      datesHint: 'Sélectionnez dates et voyageurs pour continuer',
      referencePrice: 'Prix de référence',
      total: 'Total',
      genericError: 'Une erreur est survenue.',
      night: 'nuit',
      nights: 'nuits',
      adult: 'adulte',
      adults: 'adultes',
      child: 'enfant',
      children: 'enfants',
      roomsPlural: 'chambres',
    };
    const i18nEl = this.querySelector('#voyage-booking-i18n');
    if (i18nEl) {
      try {
        this.i18n = { ...this.i18n, ...JSON.parse(i18nEl.textContent) };
      } catch {
        // keep the French fallback
      }
    }

    // Largest room capacity gates the dates-step room minimum; the per-room check happens later.
    this.maxCapacity = 2;
    const caps = (this.bookingData?.rooms || []).map((room) => room.capacity || 2);
    if (caps.length) this.maxCapacity = Math.max(...caps);

    // Step track (absent on the Namastay iframe variant).
    this.track = this.querySelector('.voyage-booking__track');
    this.stepIndex = {};
    if (this.track) {
      this.querySelectorAll('[data-voyage-step]').forEach((step, i) => {
        this.stepIndex[step.getAttribute('data-voyage-step')] = i;
      });
      this.flow = [{ step: 'dates', slot: 0 }];
      this.bookingRooms = [];
      this.currentSlot = 0;
      this.addEventListener('click', this.#handleStepClick);
      this.#initSteppers();
      this.#initCalendar();
      this.#setActiveStep('dates');
    }
  }

  /** Make every step but the active one inert, so off-screen steps aren't tabbable or SR-reachable. */
  #setActiveStep(step) {
    this.querySelectorAll('[data-voyage-step]').forEach((el) => {
      el.inert = el.getAttribute('data-voyage-step') !== step;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick);
    this.removeEventListener('click', this.#handleStepClick);
  }

  /** Push a frame (step, starting room slot, and how many slots it covers) and render it. */
  #goTo(step, slot, span = 1) {
    this.flow.push({ step, slot, span });
    this.#enterFrame(this.flow[this.flow.length - 1]);
  }

  /** Pop to the previous frame, restoring its step, slot and span. */
  goBack() {
    if (!this.track || this.flow.length <= 1) return;
    this.flow.pop();
    this.#enterFrame(this.flow[this.flow.length - 1]);
  }

  #enterFrame(frame) {
    this.currentSlot = frame.slot;
    this.currentSpan = frame.span || 1;
    this.track.style.setProperty('--voyage-step', this.stepIndex[frame.step]);
    this.#setActiveStep(frame.step);
    if (frame.step === 'rooms') this.#renderRoomChoices();
    else if (frame.step === 'detail') this.#renderDetail();
    else if (frame.step === 'recap') this.#renderRecap();
    this.#focusActiveStep();
  }

  /** Reset to the dates step with no selection or room assignments. */
  resetSteps() {
    if (!this.track) return;
    this.flow = [{ step: 'dates', slot: 0, span: 1 }];
    this.currentSlot = 0;
    this.currentSpan = 1;
    this.bookingRooms = [];
    this.track.style.setProperty('--voyage-step', 0);
    this.#setActiveStep('dates');
    if (this.nightsByDate) {
      this.checkIn = null;
      this.checkOut = null;
      this.#paintSelection();
      this.#updateDatesFooter();
    }
  }

  /** Begin room selection after the dates step: fix the room count and enter the first slot. */
  #startRoomFlow() {
    this.roomCount = this.#roomCount();
    this.bookingRooms = new Array(this.roomCount).fill(null);
    this.#enterSlot(0);
  }

  /**
   * Enter a room slot. When one room is the only valid option it is forced, so it fills
   * every remaining slot it can cover and shows its detail once (a batch). When several
   * rooms are valid, the guest chooses one room for this single slot.
   */
  #enterSlot(slot) {
    const valid = this.#validRoomsForSlot(slot);
    if (!valid.length) return; // guarded upstream by the date-step gate
    if (valid.length === 1) {
      const room = valid[0];
      const span = this.#fillCount(room, slot);
      for (let i = slot; i < slot + span; i += 1) this.bookingRooms[i] = String(room.id);
      this.#goTo('detail', slot, span);
    } else {
      if (!valid.some((room) => String(room.id) === this.bookingRooms[slot])) {
        this.bookingRooms[slot] = String(valid[0].id);
      }
      this.#goTo('rooms', slot, 1);
    }
  }

  /** How many remaining slots a forced room can fill, bounded by its stock over the range. */
  #fillCount(room, slot) {
    const priorSame = this.bookingRooms.slice(0, slot).filter((id) => id === String(room.id)).length;
    let minStock = Infinity;
    const cursor = this.#parseDate(this.checkIn);
    const end = this.#parseDate(this.checkOut);
    while (cursor < end) {
      const night = room._byDate.get(this.#isoOf(cursor));
      minStock = Math.min(minStock, night && night.available ? night.stock || 0 : 0);
      cursor.setDate(cursor.getDate() + 1);
    }
    const remaining = this.roomCount - slot;
    return Math.max(1, Math.min(minStock - priorSame, remaining));
  }

  #room(id) {
    return this.rooms.find((room) => String(room.id) === String(id)) || null;
  }

  /** Rooms that can still cover every night of the range for this slot, given earlier slots. */
  #validRoomsForSlot(slot) {
    return this.rooms.filter((room) => this.#roomValidForSlot(room, slot));
  }

  #roomValidForSlot(room, slot) {
    const priorSame = this.bookingRooms.slice(0, slot).filter((id) => id === String(room.id)).length;
    const cursor = this.#parseDate(this.checkIn);
    const end = this.#parseDate(this.checkOut);
    while (cursor < end) {
      const night = room._byDate.get(this.#isoOf(cursor));
      const stock = night && night.available ? night.stock || 0 : 0;
      if (stock < priorSame + 1) return false;
      cursor.setDate(cursor.getDate() + 1);
    }
    return true;
  }

  /** Show only the rooms valid for the current slot and keep a valid one selected. */
  #renderRoomChoices() {
    const valid = new Set(this.#validRoomsForSlot(this.currentSlot).map((room) => String(room.id)));
    let firstValid = null;
    this.querySelectorAll('.voyage-booking__room').forEach((card) => {
      const ok = valid.has(card.dataset.roomId);
      card.hidden = !ok;
      if (ok && firstValid == null) firstValid = card.dataset.roomId;
    });
    if (!valid.has(this.bookingRooms[this.currentSlot])) this.bookingRooms[this.currentSlot] = firstValid;
    this.#highlightRoom(this.bookingRooms[this.currentSlot]);
  }

  #highlightRoom(id) {
    this.querySelectorAll('.voyage-booking__room').forEach((card) => {
      card.classList.toggle('voyage-booking__room--selected', card.dataset.roomId === id);
    });
  }

  /**
   * Bind the detail step's title, nightly price and CTA wording to the slot's room.
   *
   * The innerHTML writes here (description, amenities, tarif conditions) are safe:
   * every value comes from the store-controlled metaobject data island serialised by
   * Liquid (`| json` / `| metafield_tag`), never from user input. Special requests —
   * the one user-entered field — ride as a cart line-item property, not into the DOM.
   */
  #renderDetail() {
    const room = this.#room(this.bookingRooms[this.currentSlot]);
    if (!room) return;
    const titleEl = this.querySelector('[data-detail-title]');
    if (titleEl) titleEl.textContent = room.title;

    let minPrice = null;
    let compareLabel = '';
    const cursor = this.#parseDate(this.checkIn);
    const end = this.#parseDate(this.checkOut);
    while (cursor < end) {
      const night = room._byDate.get(this.#isoOf(cursor));
      if (night) {
        if (minPrice == null || night.price < minPrice) minPrice = night.price;
        if (night.compareAt && night.compareAt > night.price) compareLabel = this.#money(night.compareAt);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    const amountEl = this.querySelector('[data-detail-amount]');
    if (amountEl) amountEl.textContent = minPrice != null ? `${this.#money(minPrice)}${this.i18n.perNight}` : '';
    const compareEl = this.querySelector('[data-detail-compare]');
    if (compareEl) {
      compareEl.textContent = compareLabel;
      compareEl.hidden = !compareLabel;
    }

    // Gallery (room images) — hidden when the room has none.
    const gallery = this.querySelector('[data-detail-gallery]');
    if (gallery) {
      const images = room.images || [];
      gallery.innerHTML = images
        .map((src) => `<img class="voyage-booking__detail-gallery-img" src="${src}" alt="" loading="lazy">`)
        .join('');
      gallery.hidden = images.length === 0;
    }

    // Spec chips — each hidden when its value is missing.
    this.#setSpec('bed', room.bedType && room.bedType.label ? room.bedType.label : '');
    this.#setSpec('capacity', room.capacity ? `${this.i18n.max} ${room.capacity}` : '');
    this.#setSpec('surface', room.surface ? `${room.surface} m²` : '');

    // Description (room product description), section hidden when empty. Strip tags
    // before the check so visually-empty rich text (e.g. "<p></p>") counts as empty.
    const descSection = this.querySelector('[data-detail-description-section]');
    const descEl = this.querySelector('[data-detail-description]');
    const hasDesc = room.description && room.description.replace(/<[^>]*>/g, '').trim() !== '';
    if (descEl) descEl.innerHTML = hasDesc ? room.description : '';
    if (descSection) descSection.hidden = !hasDesc;

    // Amenities (icon + label), section hidden when none.
    const amSection = this.querySelector('[data-detail-amenities-section]');
    const amList = this.querySelector('[data-detail-amenities]');
    const amenities = room.amenities || [];
    if (amList) {
      amList.innerHTML = amenities
        .map((a) => {
          const icon = a.icon
            ? `<img class="voyage-booking__amenity-icon" src="${a.icon}" alt="" aria-hidden="true">`
            : '';
          return `<li class="voyage-booking__amenity">${icon}${a.label}</li>`;
        })
        .join('');
    }
    if (amSection) amSection.hidden = amenities.length === 0;

    // Tarif conditions — room override falls back to the section-level default.
    const conditions = this.querySelector('[data-detail-conditions]');
    if (conditions) {
      const cancellation = room.cancellationTerms || this.bookingData.cancellationTerms || '';
      const payment = room.paymentTerms || this.bookingData.paymentTerms || '';
      conditions.innerHTML = [cancellation, payment].filter(Boolean).join('');
    }

    const cta = this.querySelector('[data-detail-cta]');
    if (cta) {
      const moreSlots = this.currentSlot + this.currentSpan < this.roomCount;
      cta.textContent = moreSlots ? this.i18n.nextRoom : this.i18n.seeRecap;
    }
  }

  /** Set a detail spec chip's text and toggle its visibility. */
  #setSpec(key, text) {
    const chip = this.querySelector(`[data-detail-spec="${key}"]`);
    if (!chip) return;
    const textEl = chip.querySelector('[data-detail-spec-text]');
    if (textEl) textEl.textContent = text;
    chip.hidden = !text;
  }

  /** Reconcile the steppers to their floors on load. */
  #initSteppers() {
    this.#syncOccupancy();
  }

  #capacity() {
    return this.maxCapacity || 2;
  }

  /** The lowest a stepper may hold. The rooms floor rises with the adult count over capacity. */
  #stepperFloor(key) {
    if (key === 'rooms') {
      return Math.max(1, Math.ceil(this.#stepperValue('adults') / this.#capacity()));
    }
    const stepper = this.querySelector(`[data-stepper="${key}"]`);
    return stepper ? parseInt(stepper.getAttribute('data-min') || '0', 10) : 0;
  }

  /** Apply a +/- step clamped to the stepper floor, then reconcile occupancy. */
  #adjustStepper(btn) {
    const stepper = btn.closest('.voyage-booking__stepper');
    const key = stepper.getAttribute('data-stepper');
    const valueEl = stepper.querySelector('.voyage-booking__stepper-value');
    const delta = parseInt(btn.getAttribute('data-step') || '0', 10);
    const value = Math.max(this.#stepperFloor(key), (parseInt(valueEl.textContent, 10) || 0) + delta);
    valueEl.textContent = value;
    this.#syncOccupancy();
    if (this.nightsByDate) {
      // The room count gates per-night stock, so re-grey the calendar and drop a range that no longer fits.
      this.#renderMonth();
      this.#revalidateRange();
    }
    this.#updateTravellers();
    this.#updateSummaries();
  }

  /**
   * Keep rooms and travellers consistent. Adults beyond a room's capacity raise the
   * room count (occupancy never changes price), and each minus reflects its floor.
   */
  #syncOccupancy() {
    const roomsStepper = this.querySelector('[data-stepper="rooms"]');
    if (roomsStepper) {
      const valueEl = roomsStepper.querySelector('.voyage-booking__stepper-value');
      const minRooms = this.#stepperFloor('rooms');
      if ((parseInt(valueEl.textContent, 10) || 1) < minRooms) valueEl.textContent = minRooms;
    }
    this.querySelectorAll('.voyage-booking__stepper').forEach((stepper) => {
      const key = stepper.getAttribute('data-stepper');
      const value = parseInt(stepper.querySelector('.voyage-booking__stepper-value').textContent, 10) || 0;
      const minus = stepper.querySelector('[data-step="-1"]');
      if (minus) minus.disabled = value <= this.#stepperFloor(key);
    });
  }

  #stepperValue(key) {
    const stepper = this.querySelector(`[data-stepper="${key}"]`);
    return stepper ? parseInt(stepper.querySelector('.voyage-booking__stepper-value').textContent, 10) || 0 : 0;
  }

  #travellerCount() {
    return this.#stepperValue('adults') + this.#stepperValue('children');
  }

  #roomCount() {
    return this.#stepperValue('rooms') || 1;
  }

  /** Sum the adult and child steppers into the dates-footer traveller count. */
  #updateTravellers() {
    const el = this.querySelector('[data-voyage-travellers]');
    if (el) el.textContent = this.#travellerCount();
  }

  /**
   * Build the per-date availability map from the room variants and render the
   * first bookable month. A highlighted day is a night stayed and consumes its
   * date-variant; arrival is the first highlighted night, departure the day after
   * the last. The dates step shows the cheapest room price per night, since the
   * room is chosen later.
   */
  #initCalendar() {
    this.daysEl = this.querySelector('[data-calendar-days]');
    this.monthLabelEl = this.querySelector('[data-calendar-month]');
    this.datesFooter = this.querySelector('.voyage-booking__dates-footer');
    if (!this.daysEl || !this.bookingData) return;

    // date (YYYY-MM-DD) -> { price, available, stock } across all rooms. Prices are
    // cents; the UI formats them with #money so the whole flow matches the cart.
    this.nightsByDate = new Map();
    for (const room of this.bookingData.rooms || []) {
      for (const night of room.nights || []) {
        if (!night.date) continue;
        const existing = this.nightsByDate.get(night.date);
        if (!existing) {
          this.nightsByDate.set(night.date, {
            price: night.price,
            available: !!night.available,
            stock: night.stock || 0,
          });
        } else {
          existing.available = existing.available || !!night.available;
          existing.stock += night.stock || 0;
          if (night.price < existing.price) existing.price = night.price;
        }
      }
    }

    // Per-room date lookup for room validity and the recap (each room's own prices).
    this.rooms = this.bookingData.rooms || [];
    this.rooms.forEach((room) => {
      room._byDate = new Map((room.nights || []).map((night) => [night.date, night]));
    });
    this.currency = this.bookingData.currency || 'EUR';
    this.moneyFormat = this.bookingData.moneyFormat || window.Shopify?.money_format || '{{ amount }}';

    const dates = Array.from(this.nightsByDate.keys()).sort();
    if (!dates.length) return;
    this.firstDate = this.#parseDate(dates[0]);
    this.lastDate = this.#parseDate(dates[dates.length - 1]);
    this.viewYear = this.firstDate.getFullYear();
    this.viewMonth = this.firstDate.getMonth();

    this.minNights = this.bookingData.minNights || 1;
    this.maxNights = this.bookingData.maxNights || null;
    this.todayIso = this.#isoOf(new Date());
    this.checkIn = null;
    this.checkOut = null;

    this.#renderMonth();
    this.#updateDatesFooter();
    this.#updateSummaries();
  }

  #parseDate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  #isoOf(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  #lang() {
    return document.documentElement.lang || 'fr';
  }

  /** A night is bookable if it has a variant, is available, is not past, and has stock for the room count. */
  #nightBookable(iso, rooms) {
    const night = this.nightsByDate.get(iso);
    return !!(night && night.available && iso >= this.todayIso && (night.stock || 0) >= rooms);
  }

  /** A day is selectable if it is a bookable night, or the morning after one (a valid departure). */
  #dayClickable(iso, rooms) {
    if (this.#nightBookable(iso, rooms)) return true;
    const prev = this.#parseDate(iso);
    prev.setDate(prev.getDate() - 1);
    return this.#nightBookable(this.#isoOf(prev), rooms);
  }

  /** Render the current view month's day grid from the availability map. */
  #renderMonth() {
    const rooms = this.#roomCount();
    const monthStart = new Date(this.viewYear, this.viewMonth, 1);
    const label = monthStart.toLocaleDateString(this.#lang(), { month: 'long', year: 'numeric' });
    if (this.monthLabelEl) this.monthLabelEl.textContent = label.charAt(0).toUpperCase() + label.slice(1);

    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const offset = monthStart.getDay(); // 0 = Sunday, matches the weekday header

    let html = '';
    for (let i = 0; i < offset; i += 1) {
      html += '<span class="voyage-booking__day voyage-booking__day--empty"></span>';
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      const iso = this.#isoOf(new Date(this.viewYear, this.viewMonth, d));
      const night = this.nightsByDate.get(iso);
      const priceLabel = night ? this.#moneyShort(night.price) : '';
      if (this.#dayClickable(iso, rooms)) {
        // Bookable night, or a valid departure day (which may have no variant of its own).
        html += `<button type="button" class="voyage-booking__day" data-date="${iso}" aria-pressed="false" aria-label="${this.#formatLong(iso)}">
          <span class="voyage-booking__day-box">
            <span class="voyage-booking__day-num">${d}</span>
            <span class="voyage-booking__day-price">${priceLabel}</span>
          </span>
        </button>`;
      } else {
        // Unavailable variant or no variant at all — shown greyed, not selectable.
        html += `<span class="voyage-booking__day voyage-booking__day--disabled">
          <span class="voyage-booking__day-box">
            <span class="voyage-booking__day-num">${d}</span>
            <span class="voyage-booking__day-price">${priceLabel}</span>
          </span>
        </span>`;
      }
    }
    this.daysEl.innerHTML = html;
    this.#paintSelection();
    this.#updateMonthNav();
  }

  #changeMonth(delta) {
    const next = new Date(this.viewYear, this.viewMonth + delta, 1);
    this.viewYear = next.getFullYear();
    this.viewMonth = next.getMonth();
    this.#renderMonth();
  }

  /** Disable month arrows at the edges of the bookable range. */
  #updateMonthNav() {
    const view = new Date(this.viewYear, this.viewMonth, 1);
    const first = new Date(this.firstDate.getFullYear(), this.firstDate.getMonth(), 1);
    const last = new Date(this.lastDate.getFullYear(), this.lastDate.getMonth(), 1);
    const prev = this.querySelector('[data-calendar-prev]');
    const next = this.querySelector('[data-calendar-next]');
    if (prev) prev.disabled = view <= first;
    if (next) next.disabled = view >= last;
  }

  #handleDayClick(btn) {
    const iso = btn.dataset.date;
    if (!iso) return;
    const rooms = this.#roomCount();

    if (this.checkIn == null || this.checkOut != null) {
      // No range yet, or a complete one — start fresh. Check-in must be a stayable night.
      if (!this.#nightBookable(iso, rooms)) return;
      this.checkIn = iso;
      this.checkOut = null;
    } else if (iso <= this.checkIn) {
      // Same or earlier than the open check-in — restart there (must be a stayable night).
      if (!this.#nightBookable(iso, rooms)) return;
      this.checkIn = iso;
    } else if (this.#rangeHasUnavailable(this.checkIn, iso) || this.#nightsBetween(this.checkIn, iso) > (this.maxNights || Infinity)) {
      // Stay spans an unavailable night or exceeds the max — restart only if this is a stayable night.
      if (!this.#nightBookable(iso, rooms)) return;
      this.checkIn = iso;
      this.checkOut = null;
    } else {
      // This day is the departure; nights stayed are [check-in, this day - 1].
      this.checkOut = iso;
    }

    this.#paintSelection();
    this.#updateDatesFooter();
  }

  /** Nights between check-in and departure (the departure day is not a stayed night). */
  #nightsBetween(from, to) {
    return Math.round((this.#parseDate(to) - this.#parseDate(from)) / 86400000);
  }

  /** True if any stayed night in `[from, to)` (departure `to` excluded) can't be booked for the room count. */
  #rangeHasUnavailable(from, to) {
    const rooms = this.#roomCount();
    const cursor = this.#parseDate(from);
    const end = this.#parseDate(to);
    while (cursor < end) {
      if (!this.#nightBookable(this.#isoOf(cursor), rooms)) return true;
      cursor.setDate(cursor.getDate() + 1);
    }
    return false;
  }

  /** Clear a complete range that no longer fits the current room count (e.g. after adding rooms). */
  #revalidateRange() {
    if (this.checkIn != null && this.checkOut != null && this.#rangeHasUnavailable(this.checkIn, this.checkOut)) {
      this.checkIn = null;
      this.checkOut = null;
      this.#paintSelection();
      this.#updateDatesFooter();
    }
  }

  /** Paint selected/start/end/range classes; the departure day shows "Départ" instead of its price. */
  #paintSelection() {
    this.daysEl.querySelectorAll('.voyage-booking__day').forEach((el) => {
      el.classList.remove(
        'voyage-booking__day--selected',
        'voyage-booking__day--start',
        'voyage-booking__day--end',
        'voyage-booking__day--range'
      );
      const iso = el.dataset.date;
      if (!iso) return;
      const priceEl = el.querySelector('.voyage-booking__day-price');
      const night = this.nightsByDate.get(iso);
      const defaultPrice = night ? this.#moneyShort(night.price) : '';
      let isDeparture = false;
      if (this.checkIn != null && this.checkOut != null) {
        if (iso === this.checkIn) el.classList.add('voyage-booking__day--selected', 'voyage-booking__day--start');
        else if (iso === this.checkOut) {
          el.classList.add('voyage-booking__day--selected', 'voyage-booking__day--end');
          isDeparture = true;
        } else if (iso > this.checkIn && iso < this.checkOut) el.classList.add('voyage-booking__day--range');
      } else if (this.checkIn != null && iso === this.checkIn) {
        el.classList.add('voyage-booking__day--selected');
      }
      if (priceEl) priceEl.textContent = isDeparture ? this.i18n.departure : defaultPrice;
      if (el.tagName === 'BUTTON') {
        const pressed =
          el.classList.contains('voyage-booking__day--selected') || el.classList.contains('voyage-booking__day--range');
        el.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      }
    });
  }

  /**
   * Toggle the empty/active footer and fill arrival/departure when the range is
   * complete and meets the minimum stay. The check-out day is the departure.
   */
  #updateDatesFooter() {
    if (!this.datesFooter) return;
    const complete =
      this.checkIn != null && this.checkOut != null && this.#nightsBetween(this.checkIn, this.checkOut) >= this.minNights;
    this.datesFooter.classList.toggle('voyage-booking__dates-footer--empty', !complete);

    const submit = this.datesFooter.querySelector('[data-voyage-next]');
    if (submit) submit.disabled = !complete;

    // When a range is picked but below the minimum stay, say so instead of the generic hint.
    const hint = this.datesFooter.querySelector('[data-voyage-hint]');
    if (hint) {
      const belowMin =
        this.checkIn != null && this.checkOut != null && this.#nightsBetween(this.checkIn, this.checkOut) < this.minNights;
      hint.textContent = belowMin ? this.i18n.minStay : this.i18n.datesHint;
    }

    if (complete) {
      const arrival = this.datesFooter.querySelector('[data-voyage-arrival]');
      const departure = this.datesFooter.querySelector('[data-voyage-departure]');
      if (arrival) arrival.textContent = this.#formatLong(this.checkIn);
      if (departure) departure.textContent = this.#formatLong(this.checkOut);
    }

    this.#updateTravellers();
    this.#updateSummaries();
  }

  #formatLong(iso) {
    return this.#parseDate(iso).toLocaleDateString(this.#lang(), { day: 'numeric', month: 'long', year: 'numeric' });
  }

  /** Format cents with the shop's money format, matching the cart and the rest of the storefront. */
  #money(cents) {
    return formatMoney(cents || 0, this.moneyFormat, this.currency);
  }

  /** Like #money but drops a trailing all-zero decimal group, for the dense calendar grid only. */
  #moneyShort(cents) {
    return this.#money(cents).replace(/[.,]0+(?=\D*$)/, '');
  }

  /** Fill the dates/travellers/rooms summary that repeats across the rooms, detail and recap headers. */
  #updateSummaries() {
    if (this.checkIn != null && this.checkOut != null) {
      const departure = this.#parseDate(this.checkOut);
      const arrival = this.#parseDate(this.checkIn).toLocaleDateString(this.#lang(), { day: 'numeric', month: 'short' });
      const depart = departure.toLocaleDateString(this.#lang(), { day: 'numeric', month: 'short', year: '2-digit' });
      this.querySelectorAll('[data-summary-dates]').forEach((el) => {
        el.textContent = `${arrival} - ${depart}`;
      });
    }
    const travellers = this.#travellerCount();
    this.querySelectorAll('[data-summary-travellers]').forEach((el) => {
      el.textContent = travellers;
    });
    const rooms = this.#roomCount();
    this.querySelectorAll('[data-summary-rooms]').forEach((el) => {
      el.textContent = rooms;
    });
  }

  /** Sum a room's own nightly prices over the range (one room, before quantity). */
  #roomRangeTotals(room) {
    let subtotal = 0;
    let reference = 0;
    let hasCompare = false;
    const cursor = this.#parseDate(this.checkIn);
    const end = this.#parseDate(this.checkOut);
    while (cursor < end) {
      const night = room._byDate.get(this.#isoOf(cursor));
      const price = night ? night.price : 0;
      const compare = night && night.compareAt ? night.compareAt : price;
      subtotal += price;
      reference += compare;
      if (night && night.compareAt && night.compareAt > price) hasCompare = true;
      cursor.setDate(cursor.getDate() + 1);
    }
    return { subtotal, reference, hasCompare };
  }

  /**
   * Render the recap by aggregating the assigned rooms. Identical rooms are grouped
   * with a quantity; the reference row shows only when a grouped room carries a struck
   * price, and the total sums every room over the chosen range.
   */
  #renderRecap() {
    if (this.checkIn == null || this.checkOut == null) return;

    const departure = this.#parseDate(this.checkOut);
    const rangeLabel = `${this.#formatLong(this.checkIn)} → ${this.#formatLong(this.#isoOf(departure))}`;
    const nights = this.#nightsBetween(this.checkIn, this.checkOut);
    const nightWord = nights > 1 ? this.i18n.nights : this.i18n.night;

    // Group assigned rooms by id, preserving selection order.
    const groups = [];
    this.bookingRooms.filter(Boolean).forEach((id) => {
      const found = groups.find((group) => group.id === id);
      if (found) found.qty += 1;
      else groups.push({ id, qty: 1, room: this.#room(id) });
    });

    let grand = 0;
    let grandRef = 0;
    let anyCompare = false;
    let cards = '';
    let rows = '';
    groups.forEach((group) => {
      if (!group.room) return;
      const totals = this.#roomRangeTotals(group.room);
      const groupSub = totals.subtotal * group.qty;
      grand += groupSub;
      grandRef += totals.reference * group.qty;
      if (totals.hasCompare) anyCompare = true;
      const qtyLabel = group.qty > 1 ? ` × ${group.qty}` : '';
      const media = group.room.image ? ` style="background-image: url(${group.room.image})"` : '';
      cards += `<div class="voyage-booking__recap-room">
        <span class="voyage-booking__recap-room-media"${media}></span>
        <span class="voyage-booking__recap-room-info">
          <span class="voyage-booking__recap-room-name">${group.room.title}${qtyLabel}</span>
          <span class="voyage-booking__recap-room-meta">${rangeLabel}</span>
          <span class="voyage-booking__recap-room-meta">${nights} ${nightWord}${group.qty > 1 ? ` · ${group.qty} ${this.i18n.roomsPlural}` : ''}</span>
        </span>
      </div>`;
      rows += `<div class="voyage-booking__recap-row"><span>${group.room.title}${qtyLabel}</span><span>${this.#money(groupSub)}</span></div>`;
    });

    const roomsEl = this.querySelector('[data-recap-rooms]');
    if (roomsEl) roomsEl.innerHTML = cards;

    const priceEl = this.querySelector('[data-recap-price]');
    if (priceEl) {
      let html = rows;
      if (anyCompare) {
        html += `<div class="voyage-booking__recap-row voyage-booking__recap-row--muted"><span>${this.i18n.referencePrice}</span><span class="voyage-booking__recap-strike">${this.#money(grandRef)}</span></div>`;
      }
      html += `<div class="voyage-booking__recap-row voyage-booking__recap-total"><span>${this.i18n.total} <span class="voyage-booking__recap-total-note">${this.bookingData.taxesIncluded || ''}</span></span><span>${this.#money(grand)}</span></div>`;
      priceEl.innerHTML = html;
    }

    const conditions = this.querySelector('[data-recap-conditions]');
    if (conditions) {
      conditions.innerHTML = [this.bookingData.cancellationTerms, this.bookingData.paymentTerms].filter(Boolean).join('');
    }
  }

  /**
   * Add the stay to the cart as nested lines: the €0 shell variant is the parent,
   * each room-night variant a child via parent_id (quantity = that room's count).
   * Dates, occupancy and special requests ride on the parent as line-item properties.
   */
  async #addToCart(button) {
    const parentId = this.bookingData?.parentVariantId;
    if (this.checkIn == null || this.checkOut == null || !parentId) {
      this.#showAddError(this.i18n.genericError);
      return;
    }

    const departure = this.#parseDate(this.checkOut);
    const adults = this.#stepperValue('adults');
    const children = this.#stepperValue('children');
    const requestsEl = this.querySelector('[data-voyage-requests]');
    const requests = requestsEl ? requestsEl.value.trim() : '';

    // Unique per-booking id so two bookings never merge into the same cart line, even when
    // they share night-variants and properties (line keys derive from variant + properties).
    const bookingId = `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

    const parentProps = { Dates: `${this.#formatLong(this.checkIn)} → ${this.#formatLong(this.#isoOf(departure))}` };
    let occupancy = `${adults} ${adults > 1 ? this.i18n.adults : this.i18n.adult}`;
    if (children > 0) occupancy += `, ${children} ${children > 1 ? this.i18n.children : this.i18n.child}`;
    parentProps.Voyageurs = occupancy;
    if (requests) parentProps['Demandes spéciales'] = requests;
    parentProps._booking = bookingId;

    const items = [{ id: Number(parentId), quantity: 1, properties: parentProps }];

    // Group identical rooms, then one child line per night at that room's quantity.
    const groups = [];
    this.bookingRooms.filter(Boolean).forEach((id) => {
      const found = groups.find((group) => group.id === id);
      if (found) found.qty += 1;
      else groups.push({ id, qty: 1 });
    });
    groups.forEach((group) => {
      const room = this.#room(group.id);
      if (!room) return;
      const cursor = this.#parseDate(this.checkIn);
      const end = this.#parseDate(this.checkOut);
      while (cursor < end) {
        const night = room._byDate.get(this.#isoOf(cursor));
        if (night && night.id) {
          items.push({ id: Number(night.id), quantity: group.qty, parent_id: Number(parentId), properties: { _night: this.#isoOf(cursor), _booking: bookingId } });
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    });

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    this.#showAddError('');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (data.status) {
        this.#showAddError(data.description || data.message || this.i18n.genericError);
        return;
      }

      // Cart count treats one stay as one item: count lines without a parent (children are skipped).
      let itemCount = 0;
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        itemCount = (cart.items || []).reduce((sum, line) => (line.parent_relationship ? sum : sum + line.quantity), 0);
      } catch {
        itemCount = 0;
      }

      // Close the booking dialog fully before announcing the add, so the cart drawer
      // opens into a clean no-modal state rather than stacking on a closing modal.
      await this.closeDialog();
      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'voyage-booking', { source: 'voyage-booking', itemCount, sections: data.sections })
      );
    } catch (error) {
      console.error(error);
      this.#showAddError(this.i18n.genericError);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  #showAddError(message) {
    const el = this.querySelector('[data-voyage-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  #handleStepClick = (event) => {
    const stepperBtn = event.target.closest('.voyage-booking__stepper-btn');
    if (stepperBtn && this.contains(stepperBtn)) {
      this.#adjustStepper(stepperBtn);
      return;
    }

    const prevMonth = event.target.closest('[data-calendar-prev]');
    if (prevMonth && this.contains(prevMonth)) {
      this.#changeMonth(-1);
      return;
    }

    const nextMonth = event.target.closest('[data-calendar-next]');
    if (nextMonth && this.contains(nextMonth)) {
      this.#changeMonth(1);
      return;
    }

    const dayBtn = event.target.closest('button.voyage-booking__day');
    if (dayBtn && this.contains(dayBtn)) {
      this.#handleDayClick(dayBtn);
      return;
    }

    const back = event.target.closest('.voyage-booking__nav-btn');
    if (back && this.contains(back)) {
      this.goBack();
      return;
    }

    const room = event.target.closest('.voyage-booking__room');
    if (room && this.contains(room)) {
      // Room card only assigns the current slot; the CTA advances to its detail.
      this.bookingRooms[this.currentSlot] = room.dataset.roomId;
      this.#highlightRoom(room.dataset.roomId);
      return;
    }

    const next = event.target.closest('[data-voyage-next]');
    if (next && this.contains(next)) {
      const from = next.closest('[data-voyage-step]')?.getAttribute('data-voyage-step');
      if (from === 'dates') {
        this.#startRoomFlow();
      } else if (from === 'rooms') {
        this.#goTo('detail', this.currentSlot, 1);
      } else if (from === 'detail') {
        // Advance past the slots this detail covered, then to the recap.
        const nextSlot = this.currentSlot + this.currentSpan;
        if (nextSlot < this.roomCount) this.#enterSlot(nextSlot);
        else this.#goTo('recap', this.currentSlot);
      }
      return;
    }

    const add = event.target.closest('[data-voyage-add]');
    if (add && this.contains(add) && !add.disabled) {
      this.#addToCart(add);
    }
  };

  #handleTriggerClick = (event) => {
    const trigger = event.target.closest('[data-voyage-reserve]');
    if (!trigger) return;
    event.preventDefault();
    this.showDialog();
  };

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.resetSteps();
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());
    this.#focusActiveStep();

    this.#dialogController = new AbortController();
    const { signal } = this.#dialogController;
    this.addEventListener('click', this.#handleOutsideClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  /** Move focus into the active (non-inert) step so keyboard and screen-reader users follow the transition. */
  #focusActiveStep() {
    const dialog = this.refs?.dialog;
    if (!dialog || !dialog.open) return;
    const active = this.querySelector('[data-voyage-step]:not([inert])');
    if (!active) return;
    // Prefer the step heading (announces the step) over the first control (the close/back button).
    // preventScroll: the steps are a transform-based carousel inside an overflow:hidden track;
    // a scrolling focus would shift the track off its translateX and desync the visible step.
    const heading = active.querySelector('.voyage-booking__step-title');
    if (heading) {
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      return;
    }
    const focusable = active.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) focusable.focus({ preventScroll: true });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.#dialogController?.abort();
    this.#dialogController = null;

    // Restart the closing animation cleanly
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.documentElement.removeAttribute('scroll-lock');
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  #handleOutsideClick = (event) => {
    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };
}

if (!customElements.get('voyage-booking')) {
  customElements.define('voyage-booking', VoyageBooking);
}
